import {
  BaseCard,
  Primary,
  SvgIcon,
  Text,
  TextLink,
} from '@cinch-labs/design-system'
import styles from './choose-store-card.module.css'
import { MouseEventHandler, useEffect } from 'react'
import {
  useAnalytics,
  EventNames,
  TrackingEventTypes,
} from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import { Coordinates } from '../../hooks/types'

type StoreCardProps = {
  index: number
  distance: number
  name: string
  address: string
  coordinates: Coordinates
  onSelect: () => void
  isSelected: boolean
  onMapClick: () => void
  showMapLink: boolean
  customerPostcode?: string
  ariaLabel?: string
}

export const ChooseStoreCard: React.FC<StoreCardProps> = ({
  index,
  distance,
  name,
  address,
  coordinates,
  onSelect,
  isSelected,
  onMapClick,
  showMapLink,
  customerPostcode,
  ariaLabel,
}) => {
  const { trackEvent } = useAnalytics()

  useEffect(() => {
    if (index === 0 && distance > 0) {
      trackEvent({
        eventName: `Load stores`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.load,
            category: 'cinch_servicing',
            action: 'store_list_displayed',
            label: 'make_a_booking',
            type: name,
            value: distance,
          },
        },
      })
    }
  }, [index])

  const onCardClick = (event: { target: { id: string } }) => {
    if (
      event.target.id !== 'choose-service-centre-button' &&
      event.target.id !== 'choose-service-centre-button-text' &&
      event.target.id !== 'service-centre-map-link'
    ) {
      onMapClick()
    }
  }

  return (
    <BaseCard
      className={cx(
        styles.card,
        index === 0 ? '' : styles.marginTop,
        isSelected ? styles.selected : '',
      )}
    >
      <div
        onClick={onCardClick as unknown as MouseEventHandler<HTMLDivElement>}
      >
        <Text
          element="h3"
          fontSizeFixed="xl"
          fontWeight="bold"
          className={styles.name}
        >
          {name}
        </Text>

        <Text fontSizeFixed="s" className={styles.address}>
          {address}
        </Text>

        {distance !== 0 && (
          <Text fontSizeFixed="s" className={styles.distance}>
            {distance} miles away
          </Text>
        )}

        {showMapLink && customerPostcode && (
          <div className={styles.mapLink}>
            <TextLink
              onClick={onMapClick}
              element={'a'}
              href={`https://www.google.com/maps/dir/?api=1&origin=${customerPostcode}&destination=${coordinates.latitude},${coordinates.longitude}`}
              target={'_blank'}
              data-testid={'service-centre-map-link'}
              id={'service-centre-map-link'}
            >
              <Text fontSizeFixed="s">
                <SvgIcon name="new-tab" /> Map and directions
              </Text>
            </TextLink>
          </div>
        )}

        <Primary
          onClick={onSelect}
          size="large"
          classNames={{ button: styles.button }}
          aria-label={ariaLabel || `Choose ${name}`}
          data-testid={'choose-service-centre-button'}
          id={'choose-service-centre-button'}
        >
          <div
            data-testid={'choose-service-centre-button-text'}
            id={'choose-service-centre-button-text'}
          >
            {`Choose service centre`}
          </div>
        </Primary>
      </div>
    </BaseCard>
  )
}
